import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import { addContractor, updateContractor } from "api/contractor";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  contragent: string;
  bin: string;
};

const REQUIRED_FIELDS = ["contragent", "bin"] as const;

export default function NewContractor() {
  const { contractors, getContractorsData, notify } = useSharedState();
  const { contractorId } = useParams();
  const navigate = useNavigate();

  const contractorItem = useMemo(() => {
    return contractors.find((t) => t.contragent_id === Number(contractorId));
  }, [contractors]);

  const [formRecords, setFormRecords] = useState<FormRecords>({
    contragent: "",
    bin: "",
  });

  useEffect(() => {
    if (contractorId) {
      if (contractorItem) {
        setFormRecords({
          contragent: contractorItem.contragent,
          bin: contractorItem.bin,
        });
      } else {
        notify("Контрагент не найден", "danger");
        navigate("/contractors");
      }
    }
  }, [contractorId, contractorItem]);

  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const contractorFields = [
    {
      id: "contragent",
      title: "Наименование контрагента",
      type: "text",
      placeholder: "Введите наименование",
      maxLength: undefined,
    },
    {
      id: "bin",
      title: "БИН контрагента",
      type: "text",
      placeholder: "Введите БИН",
      maxLength: 12,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "bin") {
      // Only allow numbers and max 12 digits
      newValue = newValue.replace(/\D/g, "").slice(0, 12);
    } else {
      newValue = newValue.slice(0, 100);
    }
    setFormRecords((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const submitContractor = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      if (contractorId) {
        await updateContractor(
          Number(contractorId),
          formRecords.contragent,
          true,
          formRecords.bin,
          contractorItem?.status
        );
        notify("Контрагент обновлен", "success");
      } else {
        await addContractor(formRecords.contragent, formRecords.bin);
        notify("Контрагент создан", "success");
      }

      setFormSubmitted(true);
      getContractorsData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", contractorId ? "Ошибка при обновлении контрагента" : "Ошибка при создании контрагента");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, placeholder, maxLength }: (typeof contractorFields)[number]) => {
    return (
      <div className={"col-lg-6"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            <input
              type="text"
              className={`form-control rounded-2xl ${errors[id] ? "is-invalid" : ""}`}
              name={id}
              placeholder={placeholder}
              value={(formRecords[id] as string) || ""}
              onChange={handleInputChange}
              disabled={isFormLoading}
              {...(typeof maxLength === "number" ? { maxLength } : {})}
            />
            <div className="invalid-feedback">Введите корректное значение</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newContractorBody = (
    <div>
      <form id="contractorForm" className="row" onSubmit={submitContractor}>
        {contractorFields.map(renderField)}
      </form>
    </div>
  );

  const newContractorFooter = (
    <button type="submit" form="contractorForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
      {contractorId ? "Сохранить изменения" : "Создать контрагента"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header={contractorId ? "Редактирование контрагента" : "Новый контрагент"}
      body={newContractorBody}
      footer={newContractorFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
